import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';

const PhoneNumberInput = ({ value, onChange, error, onValidation }) => {
  const [countryCode, setCountryCode] = useState('+33');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [isValid, setIsValid] = useState(false);

  const countries = [
    { code: '+33', name: 'France', flag: '🇫🇷' },
    { code: '+32', name: 'Belgique', flag: '🇧🇪' },
    { code: '+41', name: 'Suisse', flag: '🇨🇭' },
    { code: '+1', name: 'États-Unis', flag: '🇺🇸' },
    { code: '+44', name: 'Royaume-Uni', flag: '🇬🇧' }
  ];

  const formatFrenchPhoneNumber = (number) => {
    // Remove all non-digits
    const digits = number.replace(/\D/g, '');
    
    // Format as XX XX XX XX XX for French numbers
    if (countryCode === '+33') {
      if (digits.length <= 2) return digits;
      if (digits.length <= 4) return `${digits.slice(0, 2)} ${digits.slice(2)}`;
      if (digits.length <= 6) return `${digits.slice(0, 2)} ${digits.slice(2, 4)} ${digits.slice(4)}`;
      if (digits.length <= 8) return `${digits.slice(0, 2)} ${digits.slice(2, 4)} ${digits.slice(4, 6)} ${digits.slice(6)}`;
      return `${digits.slice(0, 2)} ${digits.slice(2, 4)} ${digits.slice(4, 6)} ${digits.slice(6, 8)} ${digits.slice(8, 10)}`;
    }
    
    return digits;
  };

  const validatePhoneNumber = (number, code) => {
    const digits = number.replace(/\D/g, '');
    
    if (code === '+33') {
      // French mobile numbers: 06, 07, or 09 followed by 8 digits
      return digits.length === 10 && (digits.startsWith('06') || digits.startsWith('07') || digits.startsWith('09'));
    }
    
    // Basic validation for other countries (at least 8 digits)
    return digits.length >= 8 && digits.length <= 15;
  };

  const handlePhoneChange = (e) => {
    const inputValue = e.target.value;
    const formattedNumber = formatFrenchPhoneNumber(inputValue);
    
    setPhoneNumber(formattedNumber);
    
    const fullNumber = `${countryCode} ${formattedNumber}`;
    const valid = validatePhoneNumber(formattedNumber, countryCode);
    
    setIsValid(valid);
    onChange(fullNumber);
    onValidation(valid);
  };

  const handleCountryChange = (e) => {
    const newCountryCode = e.target.value;
    setCountryCode(newCountryCode);
    setPhoneNumber('');
    
    const fullNumber = `${newCountryCode} `;
    onChange(fullNumber);
    onValidation(false);
  };

  useEffect(() => {
    if (value) {
      const parts = value.split(' ');
      if (parts.length > 1) {
        setCountryCode(parts[0]);
        setPhoneNumber(parts.slice(1).join(' '));
      }
    }
  }, [value]);

  return (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-text-primary font-inter">
        Numéro de téléphone mobile *
      </label>
      
      <div className="flex space-x-2">
        {/* Country Code Selector */}
        <div className="relative">
          <select
            value={countryCode}
            onChange={handleCountryChange}
            className="appearance-none bg-surface border border-border rounded-lg px-3 py-3 pr-8 text-text-primary font-inter focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary transition-smooth"
          >
            {countries.map((country) => (
              <option key={country.code} value={country.code}>
                {country.flag} {country.code}
              </option>
            ))}
          </select>
          <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
            <Icon name="ChevronDown" size={16} color="var(--color-text-secondary)" />
          </div>
        </div>

        {/* Phone Number Input */}
        <div className="flex-1 relative">
          <Input
            type="tel"
            value={phoneNumber}
            onChange={handlePhoneChange}
            placeholder={countryCode === '+33' ? '06 12 34 56 78' : 'Numéro de téléphone'}
            className={`w-full ${error ? 'border-error focus:ring-error' : isValid ? 'border-success focus:ring-success' : ''}`}
          />
          
          {/* Validation Icon */}
          {phoneNumber && (
            <div className="absolute inset-y-0 right-0 flex items-center pr-3">
              {isValid ? (
                <Icon name="CheckCircle" size={20} color="var(--color-success)" />
              ) : (
                <Icon name="AlertCircle" size={20} color="var(--color-error)" />
              )}
            </div>
          )}
        </div>
      </div>

      {/* Format Hint */}
      <div className="flex items-center space-x-2 text-xs text-text-secondary font-inter">
        <Icon name="Info" size={14} />
        <span>
          {countryCode === '+33' ?'Format: 06 12 34 56 78 (numéros mobiles uniquement)' :'Entrez votre numéro de téléphone complet'
          }
        </span>
      </div>

      {/* Error Message */}
      {error && (
        <div className="flex items-center space-x-2 text-sm text-error font-inter">
          <Icon name="AlertCircle" size={16} />
          <span>{error}</span>
        </div>
      )}

      {/* Success Message */}
      {isValid && !error && phoneNumber && (
        <div className="flex items-center space-x-2 text-sm text-success font-inter">
          <Icon name="CheckCircle" size={16} />
          <span>Numéro de téléphone valide</span>
        </div>
      )}
    </div>
  );
};

export default PhoneNumberInput;